import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { isBrowser, isSmallScreen } from "../helpers/screens";
import { useAuth, useOrganizationList } from "@clerk/clerk-react";
import { useLocation, useNavigate } from "react-router";
import { useJwt } from "../helpers/common";


interface SidebarContextProps {
  isOpenOnSmallScreens: boolean;
  isPageWithSidebar: boolean;
  setOpenOnSmallScreens: (isOpen: boolean) => void;
}

const SideNavContext = createContext<SidebarContextProps>(undefined!);

const SIDEBAR_STORAGE_KEY = "sidebarIsCollapsed";

const  SideNavProvider = ({ children }: PropsWithChildren) => {
  const location = isBrowser() ? window.location.pathname : "/";
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!isBrowser()) return;
    
    const savedIsCollapsed = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (savedIsCollapsed !== null) {
      setIsCollapsed(savedIsCollapsed === "true");
    }
  }, []);

  const setOpenOnSmallScreens = (newState: boolean) => {
    setIsCollapsed(newState);
    if (isBrowser()) {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, newState.toString());
    }
  };

  useEffect(() => {
    if (isSmallScreen()) {
      setIsCollapsed(false);
    }
  }, [location]);

  useEffect(() => {
    const handleMobileTapInsideMain = (event: MouseEvent) => {
      const main = document.querySelector("main");
      const isClickInsideMain = main?.contains(event.target as Node);

      if (isSmallScreen() && isClickInsideMain) {
        setIsCollapsed(false);
      }
    };

    document.addEventListener("mousedown", handleMobileTapInsideMain);
    return () => {
      document.removeEventListener("mousedown", handleMobileTapInsideMain);
    };
  }, []);

  const { isLoaded, isSignedIn } = useAuth();
  const { JWTData, tokenLoaded }: any = useJwt(true);
  const { setActive } = useOrganizationList();
  const { org_id = "", org_list = [] } = JWTData;
  const navigate = useNavigate();
  const locationn = useLocation();

  useEffect(() => {
    if (!isLoaded) return;

    const navigateToProjectsPage = () => {
      if(locationn.pathname === "/signup") {
        navigate("/strategymaps")
      }
    }

    const fetchdata = async () => {
      if(isSignedIn 
        && Object.keys(JWTData).length > 0
      ) {
        if (!org_id) {
          if(Object.keys(org_list).length){
            const [firstOrgId = ''] = Object.keys(org_list)
            if(firstOrgId && typeof setActive == "function"){
              await setActive({ organization: firstOrgId })
              navigateToProjectsPage()
            } else {
              navigate("/signup");
            }
          } else {
            navigate("/signup");
          }
        } else {
          navigateToProjectsPage()
        }
      }
    }

    fetchdata();
  }, [isLoaded, isSignedIn, JWTData, tokenLoaded]);

  if(isSignedIn) {
    const ret = null;
    // scenarios when to show null
    // when I am on projects and I dont have orgId then go to signup
      // When I am on signup and I dont have orgId then render form
    // when i am on signup and I have orgId then go to projects 
    // when i am on signup and I dont have orgId then show null
    // Also when token loading show null
    if(locationn.pathname === "/signup") {
      if(tokenLoaded) {
        if (org_id) {
          return ret;
          // return <div className="text-white">On Signup page with org id</div>;
        }
      } else {
        // return <div className="text-white">Token loading</div>;
        return ret;
      }
    } else if(locationn.pathname === "/strategymaps" || locationn.pathname === "/") {
      if(tokenLoaded) {
        if (!org_id) {
          return ret;
          // return <div className="text-white">On Non Signup page without org id</div>;
        }
      } else {
        // return <div className="text-white">Token loading</div>;
        return ret;
      }
    }
  }

  return (
    <SideNavContext.Provider
      value={{
        isOpenOnSmallScreens: isCollapsed,
        isPageWithSidebar: true,
        setOpenOnSmallScreens: setOpenOnSmallScreens,
      }}
    >
      {children}
    </SideNavContext.Provider>
  );
}
export default SideNavProvider;

export function useSideNavContext(): SidebarContextProps {
  const context = useContext(SideNavContext);

  if (typeof context === "undefined") {
    throw new Error(
      "useSidebarContext should be used within the SidebarContext provider!"
    );
  }

  return context;
}